import React from 'react';
import { Card, Button } from '@ui-elements';
import { money, toValidUri } from 'common/utils/formatters';
import { useRecoilState } from 'recoil';
import { ModelAccessoriesPage } from '../../store';
import { AccessoryPack } from '~/common/models';
import styles from './index.module.scss';
import classNames from 'classnames';
import ImgLazyLoad from '~/common/components/image-lazy-loading';

interface Props extends AccessoryPack {
  backgroundColor?: string;
}

// The accessory bundle/pack detail page always lives directly under the
// model's root path (e.g. /solterra/accessory-packs/... or, for models with
// a model-year in the URL, /outback/2026/accessory-packs/...), regardless of
// which page the "More info" link is clicked from. Model subpages (e.g.
// /solterra/charging or /outback/2026/charging) are nested deeper than the
// model root, so building the link off the full current path left the
// subpage's extra slug in the URL and produced a 404. Derive the base path
// from the model segment plus, when present, a model-year segment (matching
// the convention used in macros/variant-accessory-packs and
// macros/variant-accessories).
const getModelRootPath = (path) => {
  const [modelSegment, secondSegment] = path.split('/').filter(Boolean);
  if (!modelSegment) return '';
  const isModelYear = /^\d{4}$/.test(secondSegment ?? '');
  return isModelYear ? `/${modelSegment}/${secondSegment}` : `/${modelSegment}`;
};

const constructAccPackBasePath = (path) =>
  `${getModelRootPath(path)}/accessory-packs`;

export const AccessoryPackCard = React.memo<Props>(
  ({ backgroundColor, ...prop }) => {
    const [{ parentUrlSegment }] = useRecoilState(ModelAccessoriesPage);

    const formatAccessoryName = (name: string) => {
      return name
        .replace(/[^a-zA-Z0-9\s]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '')
        .toLowerCase();
    };

    const getUri = React.useCallback(() => {
      if (!!prop.description) {
        const formattedAccName = formatAccessoryName(prop.description);
        const cleanedPath = constructAccPackBasePath(window.location.pathname);
        return `${cleanedPath}/${prop.itemCode}/${toValidUri(
          formattedAccName
        )}`;
      }
      return '#';
    }, [prop.description]);

    return (
      <div className={styles.accessoryPack}>
        <Card
          className={styles.accessoryPackCard}
          contentClassName={styles.accessoryPackCardContent}
        >
          <div className={styles.imageWrapper}>
            <img src={prop.imageSrc} alt={prop.pimIdentifier} loading="lazy" />
          </div>
          <div className={styles.detailsWrapper} style={{ backgroundColor }}>
            <div
              className={classNames(
                prop.accessories.length === 0 && styles.fullWidth
              )}
            >
              <p className={styles.name}>{prop.description}</p>
              <p className={styles.price}>
                Fitted RRP: {money(prop.specialPriceFitted)}
              </p>
              <Button buttonSize="small" href={getUri()} target="_blank">
                More info
              </Button>
            </div>
            {prop.accessories.length > 0 && (
              <div>
                <ul className={styles.accessories}>
                  {prop.accessories?.map((acc) => (
                    <li key={acc.id}>{acc.description}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </Card>
      </div>
    );
  }
);

interface AccessoryPackItemProps extends AccessoryPack {
  backgroundColor?: string;
  modelName?: string;
}

export const AccessoryPackItem = (props: AccessoryPackItemProps) => {
  const formatAccessoryName = (name: string) => {
    return name
      .replace(/[^a-zA-Z0-9\s]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '')
      .toLowerCase();
  };

  const truncateSuperscript = (description: string) => {
    return description.replace(/[⁰¹²³⁴⁵⁶⁷⁸⁹]/g, '');
  };

  const getUri = React.useCallback(() => {
    if (!!props.description && typeof window !== 'undefined') {
      const trimmedAccName = truncateSuperscript(props.description);
      const formattedAccName = formatAccessoryName(trimmedAccName);
      const cleanedPath = constructAccPackBasePath(window.location.pathname);
      return `${cleanedPath}/${props.itemCode}/${toValidUri(formattedAccName)}`;
    }
    return '#';
  }, [props.description]);

  return (
    <div className={styles.itemWrapper}>
      <div>
        <ImgLazyLoad
          src={props.imageSrc}
          alt={props.description}
          loading="lazy"
        />
      </div>

      <div className={styles.itemContent}>
        <div>
          <div>
            <p>{props.description}</p>
            <p>{`Fitted RRP: ${money(props.specialPriceFitted)}`}</p>
          </div>
          <Button
            buttonSize="small"
            type="primary"
            href={getUri()}
            target="_blank"
          >
            More info
          </Button>
        </div>
        <div>
          <ul>
            {props.accessories.map((acc) => (
              <li key={acc.id}>{acc.description}</li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};
